import { useCallback, useMemo } from 'react';
import { BeaconEvent, sendEvent } from 'api/beacon';
import { useDisplayUnits } from 'state/displayUnits/context';
import { usePageData } from 'state/pageData';
import { useSession } from 'state/session';
import { useWidget } from 'state/widget/context';
import { useTrackingState } from '../TrackingStateContext';
import {
  AppTrackingEvent,
  DisplayedUnitAttributes,
  PartialEvent,
} from '../types';
import { isSponsored } from '../utils';
import { getResultProp } from './utils';

export type CommonEvent = Pick<
  BeaconEvent,
  'domainId' | 'prId' | 'sessionId' | 'url' | 'variantId' | 'widgetId'
>;

export default function useDefaultTrackingDispatch() {
  const state = useTrackingState();
  const { url, widgetId } = usePageData();
  const { sessionId } = useSession();
  const {
    state: { domainId, prId, units = {}, variantId },
  } = useWidget();

  const { displayUnits } = useDisplayUnits();

  const getDisplayedResults = useCallback(
    (displayedUnitAttributes: DisplayedUnitAttributes) =>
      displayUnits.map((resultId) =>
        getResultProp(resultId, units, displayedUnitAttributes),
      ),
    [displayUnits, units],
  );

  const baseEvent: CommonEvent = useMemo(
    () => ({ domainId, prId, sessionId, url, widgetId, variantId }),
    [domainId, prId, sessionId, url, variantId, widgetId],
  );

  const trackEvent = useCallback(
    (event: PartialEvent<BeaconEvent>) => {
      sendEvent({ ...baseEvent, ...event });
    },
    [baseEvent],
  );

  return useCallback(
    (event: AppTrackingEvent) => {
      if (event.event === 'browserTouchStart') {
        trackEvent({
          event: event.event,
          properties: {
            browserProperties: event.payload,
          },
        });
      }
      switch (event.event) {
        case 'widgetLoad':
        case 'widgetInViewport':
        case 'widgetEngaged': {
          trackEvent({
            event: event.event,
            properties: {
              displayedResults: getDisplayedResults(
                state.getDisplayedUnitAttributes(),
              ),
            },
          });
          break;
        }

        case 'spResultPlayed':
        case 'resultPlayed': {
          trackEvent({
            event: event.event,
            properties: {
              playedResult: getResultProp(
                event.payload.resultId,
                units,
                state.getDisplayedUnitAttributes(),
              ),
            },
          });
          break;
        }

        case 'spResultClickThrough':
        case 'resultClickThrough': {
          trackEvent({
            event: event.event,
            properties: {
              ctResult: getResultProp(
                event.payload.resultId,
                units,
                state.getDisplayedUnitAttributes(),
              ),
            },
          });
          break;
        }

        case 'mediaPlaybackStarted':
        case 'mediaPlaybackHeartbeat':
        case 'mediaPaused':
        case 'mediaCompleted':
        case 'mediaSeekStarted':
        case 'mediaSeekCompleted':
        case 'mediaBufferStarted':
        case 'mediaBufferCompleted': {
          trackEvent({
            event: event.event,
            properties: {
              mediaProperties: {
                resultId: event.payload.resultId,
                isSponsored: isSponsored(event.payload.resultId, units),
                position: event.payload.positionMillis,
              },
            },
          });
          break;
        }

        case 'browserMouseDown': {
          trackEvent({
            event: event.event,
            properties: {
              browserProperties: event.payload,
            },
          });
          break;
        }

        case 'browserMouseUp': {
          trackEvent({
            event: event.event,
            properties: {
              browserProperties: event.payload,
            },
          });
          break;
        }

        case 'browserTouchStart': {
          trackEvent({
            event: event.event,
            properties: {
              browserProperties: event.payload,
            },
          });
          break;
        }

        case 'browserTouchEnd': {
          trackEvent({
            event: event.event,
            properties: {
              browserProperties: event.payload,
            },
          });
          break;
        }

        default:
          // eslint-disable-next-line no-case-declarations, @typescript-eslint/naming-convention
          const _exhaustiveCheck: never = event;
          return _exhaustiveCheck;
      }
    },
    [getDisplayedResults, state, trackEvent, units],
  );
}
